<script lang="ts" setup>
import type { DynamicAuctionItemList } from '@autobid/strapi-integration/rest/components'
import type { Counters } from '@autobid/ui/types/components/CarsList'
import { useNoFilterCounters } from '@autobid/ui/composables/car/data/useNoFilterCounters'
import { injectFilterComposable } from '@autobid/ui/utils/carsList/injectFilterComposable'
import { computed, inject, onServerPrefetch, watch } from 'vue'

interface Props {
  counters: Counters
}

const props = defineProps<Props>()

const auctionStrapiData = inject<DynamicAuctionItemList>('auction-strapi-data')
const { data, suspense } = useNoFilterCounters(
  auctionStrapiData?.type === 'carList' ? 'auction' : 'searchList',
)

onServerPrefetch(suspense)
await suspense()

const useFilter = injectFilterComposable()
const {
  filters: { supplierIds },
} = useFilter()

function isChecked(value: number) {
  return Boolean(supplierIds.value?.includes(value))
}

function handleFilter(isAdding: boolean, value: number) {
  const currentValue = supplierIds.value || []

  supplierIds.value = isAdding
    ? [...currentValue, value]
    : currentValue.filter(el => el !== value)
}

const checkedAndDisabledIds = computed(() => {
  return Object.values(data.value?.counters.supplier || []).reduce((acc: string[], supplier) => {
    const checked = isChecked(supplier.id)
    const isDisabled = !props.counters.supplier?.[supplier.id]?.count

    if (checked && isDisabled) {
      acc = [...acc, String(supplier.id)]
    }

    return acc
  }, [])
})

function uncheckDisabledCategories() {
  if (!checkedAndDisabledIds.value.length)
    return

  supplierIds.value = supplierIds.value?.filter(
    el => !checkedAndDisabledIds.value.includes(String(el)),
  )
}
watch(checkedAndDisabledIds, uncheckDisabledCategories)
</script>

<template>
  <common-filters-dropdown
    v-if="Object.keys(data?.counters?.supplier ?? {}).length"
    card-key="suppliers"
  >
    <template #title>
      {{ $t('cars-list.filters.suppliers') }}
    </template>

    <template #content>
      <common-filters-checkboxes-wrapper class="!mt-0">
        <ElementsCheckbox
          v-for="supplier in data?.counters.supplier"
          :key="supplier.id"
          class="[&>*]:text-[#58585A]"
          :checked="isChecked(supplier.id)"
          :label-suffix="counters.supplier?.[supplier.id]?.count ?? 0"
          :disabled="(counters.supplier?.[supplier.id]?.count ?? 0) === 0"
          :label="supplier.label"
          :small="true"
          uncheck-if-disabled
          @change="(isAdding: boolean) => handleFilter(isAdding, supplier.id)"
        />
      </common-filters-checkboxes-wrapper>
    </template>
  </common-filters-dropdown>
</template>
